import { pt2px } from '@/utils/converter'
import React, { useRef, useEffect, useState, useMemo } from 'react'
import HTMLFlipBook from 'react-pageflip'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { LockIcon } from '../Icons'
import { useTranslation } from 'next-i18next'

const formatImageUrl = (url: string | undefined) => {
  if (!url) return '/placeholder.png';
  
  // If it's already a full URL, return it directly (for AI-generated images)
  if (url.startsWith('http')) {
    return url;
  }
  
  // For relative paths, check if it's from our uploads
  if (url.startsWith('uploads/')) {
    const cleanPath = url.replace(/^\/+/, '');
    const cdnUrl = process.env.NEXT_PUBLIC_CDN_URL?.replace(/\/+$/, '') || '';
    return `${cdnUrl}/${cleanPath}`;
  }
  
  // For other relative paths (PDF pages), add CDN URL
  const cleanPath = url.replace(/^\/+/, '');
  const cdnUrl = process.env.NEXT_PUBLIC_CDN_URL?.replace(/\/+$/, '') || '';
  return `${cdnUrl}/${cleanPath}`;
};

interface Props {
  pages: any
  pages_predefined_texts: any
  pages_dedication_texts: any
  product: any
  inputDedication: any
  cover_pages_predefined_texts: any
  cover_pages: any
  bookView?: string
  isRtl?: boolean
  width?: number
  height?: number
  minWidth?: number
  maxWidth?: number
  minHeight?: number
  maxHeight?: number
  desktopRef?: any
  imageSelected?: any
  dedicationImage?: any
  isProductRtlInBook?: any
  isInIsrael?: any
  visiblePageCount?: number, // Add this prop for controlling visible pages
  isAIStory?: boolean
  isAIGenerating?: boolean
  aiLoadingStep?: number
  dedicationImageMode?: any
  aiStoryImage?: any
  isEdit?: boolean
}
export default function AIBook({
  pages,
  pages_predefined_texts,
  isRtl = true,
  pages_dedication_texts,
  product,
  inputDedication,
  cover_pages,
  cover_pages_predefined_texts,
  imageSelected,
  dedicationImage,
  width = 600,
  height = 600,
  minWidth = 415,
  maxWidth = 600,
  minHeight = 600,
  maxHeight = 833,
  desktopRef,
  bookView = 'double',
  isProductRtlInBook,
  isInIsrael,
  visiblePageCount = Infinity, // Default to show all pages
  isAIStory,
  isAIGenerating,
  aiLoadingStep = 0,
  dedicationImageMode,
  aiStoryImage,
  isEdit=false
}: Props) {
  const { t } = useTranslation('personalizeModal')
  // Use useMemo to prevent re-renders
  const aiLoadingSteps = useMemo(() => [
    t('aiGeneratingStory'),
    t('aiMakingHero'),
    t('aiAddingCharacters'),
    t('aiFinalizingIllustrations')
  ], [t]);
  
  const modified_pages: any = []
  const [forceUpdate, setForceUpdate] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const router = useRouter()
  const {locale} = useRouter();
  const name =
    router.query.name != undefined ? (router.query.name as string) : ''
  const [imageWidth, setImageWidth] = React.useState(300)
  
  // FIXED: Always use correct indices for covers
  const firstCoverImage = cover_pages[0]; // Always index 0 for first cover
  const lastCoverImage = cover_pages[cover_pages && cover_pages.length > 1 ? 1 : 0]; // Index 1 for last cover

  // Force recalculation when bookView changes
  useEffect(() => {
    setForceUpdate(prev => prev + 1);
    
    // Recalculate image width after a short delay to ensure DOM is updated
    setTimeout(() => {
      const elem = document.querySelector('.--simple img')
      if (elem && elem.clientWidth !== 0) {
        setImageWidth(elem.clientWidth)
      }
    }, 100);
  }, [bookView]);

  const handleImageLoad = (event: any) => {
    const elem = document.querySelector('.--simple img')
    if (elem) {
      if (elem.clientWidth != 0) {
        setImageWidth(elem.clientWidth)
      }
    }
  }

  // Effect to cycle through loading steps at regular intervals
  useEffect(() => {
    if (!isAIStory || !isAIGenerating) return;
    
    const interval = setInterval(() => {
      setCurrentStepIndex(prev => (prev + 1) % aiLoadingSteps.length);
    }, 3000); // Change step every 3 seconds
    
    return () => clearInterval(interval);
  }, [isAIStory, isAIGenerating, aiLoadingSteps]);

  const renderAIGeneratingOverlay = (pageIndex: number) => {
    if (!isAIStory || !isAIGenerating) return null;
    
    // Only show on pages that are visible (first 5 pages)
    if ((modified_pages.length - pageIndex) > visiblePageCount) return null;
    
    return (
      <div className="absolute inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-sm" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="p-4 max-w-xs w-full mx-4">
          <div className="text-center">
            <h2 className="text-xl font-bold text-primary mb-3">
              {t('aiStoryGenerating')}
            </h2>
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-primary text-md leading-6 min-h-[3rem] line-clamp-2 animate-popup" key={aiLoadingSteps[currentStepIndex]}>
              {aiLoadingSteps[currentStepIndex]}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Function to render lock overlay - only show when book is loaded (isAIGenerating is false)
  const renderAIStoryLockOverlay = (pageIndex: number) => {
    // Check if this page should be blurred
    const shouldBeBlurred = shouldBlurPage(pageIndex, isProductRtlInBook, false, isEdit);
    if (!shouldBeBlurred) return null;
    
    return (
      <div className="absolute inset-0 z-[70] flex flex-col items-center justify-center backdrop-blur-md bg-white/40" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="p-6 max-w-[80%] flex justify-center items-center">
          <div className="flex flex-col items-center justify-center gap-3">
            <LockIcon />
            <p className={`text-primary text-center font-medium lg:text-2xl mt-4`}>
              {t('premiumBuyText')}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Function to check if page should have lock overlay
  const shouldBlurPage = (pageIndex: number, isRTL: boolean, isCover: boolean = false, isEditMode: boolean = false): boolean => {
    if (isCover) return false;
    
    // Check if we're limiting visible pages
    if (isAIStory && visiblePageCount < Infinity) {
      // Calculate effective page number based on layout
      let effectivePageNumber: number;
      
      if (isProductRtlInBook && isInIsrael) {
        // In RTL Israel mode
        const totalContentPages = modified_pages.length;
        effectivePageNumber = totalContentPages - pageIndex;
      } else {
        // For other layouts
        effectivePageNumber = pageIndex;
      }
      
      // In edit mode, pages are already reversed, so we need to adjust
      if (isEditMode) {
        // In edit mode, the first few pages (after reversal) should be visible
        // So we want to blur pages beyond visiblePageCount from the beginning
        return pageIndex > visiblePageCount;
      } else {
        // Original logic for non-edit mode
        return effectivePageNumber > visiblePageCount;
      }
    }
    
    return false;
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', () => {
      if (window.innerWidth > 768) {
        const elem = document.querySelector('.--simple img')
        if (elem) {
          if (elem.clientWidth != 0) {
            setImageWidth(elem.clientWidth)
          }
        }
      }
    })
  }

  React.useEffect(() => {
    if (imageWidth === 0) {
      const elem = document.querySelector('.--simple img')
      if (elem) {
        if (elem.clientWidth != 0) {
          setImageWidth(elem.clientWidth)
        }
      }
    }
  }, [imageWidth, bookView]) // Add bookView dependency

  const getCalculatedXCoord = (X_coord_percent: number) => {
    const cover_calculation = JSON.parse(
      cover_pages_predefined_texts.filter(
        (x: any) => x.page == (0).toString(),
      )[0].dimensions,
    )['width']
    const X_coord_percent_calculated =
      (cover_calculation * X_coord_percent) / (cover_calculation * 2)
    return X_coord_percent_calculated
  }

  const getTextAlignment = (text_align: string) => {
    if (text_align == 'C') {
      return 'center'
    }
    if (text_align == 'R') {
      return 'right'
    }
    if (text_align == 'L') {
      return 'left'
    }
    return 'left'
  }

  if (pages) {
    //Swap two elements
    let pagesToUse = [...pages];
    for (let index = pagesToUse.length - 1; index >= 0; index--) {
      if ((index + 1) % 2 === 0) {
        continue
      }
      modified_pages.push(pagesToUse[index + 1], pagesToUse[index])
    }

    let filteredPages: any;

    // Filter out pages based on dedication image selection
    // IMPORTANT: Use the same logic as mobile version
    // The mobile version checks dedicationImageMode, not just imageSelected
    const hasDedicationImage = () => {
      if (dedicationImageMode === 'ai-image' && aiStoryImage) {
        return true;
      }
      if (dedicationImageMode === 'separate-image' && dedicationImage) {
        return true;
      }
      return false;
    };

    filteredPages = modified_pages.filter((page: any, index: number) => {
      // For RTL layout, check if we should skip this page
      if (isProductRtlInBook) {
        const currentPageIndex = isInIsrael ? (modified_pages.length - index - 1) : (modified_pages.length - index);
        // Skip the image dedication page if no image is selected
        if (currentPageIndex === 1 && !hasDedicationImage()) return false;
        // Skip the text-only dedication page if image is selected
        if (currentPageIndex === 0 && hasDedicationImage()) return false;
      }
      // For LTR layout, check if we should skip this page
      else {
        // Skip the image dedication page if no image is selected
        if (index === (modified_pages.length - 2) && !hasDedicationImage()) return false;
        // Skip the text-only dedication page if image is selected
        if (index === (modified_pages.length - 1) && hasDedicationImage()) return false;
      }
      return true;
    });

    // Get the correct dedication page based on image selection
    const dedicationPageIndex = isProductRtlInBook ?
      (hasDedicationImage() ? 1 : 2) :
      (hasDedicationImage() ? modified_pages.length - 3 : modified_pages.length - 2);

    const dedicationPage = modified_pages[dedicationPageIndex];
    const whitePage = isInIsrael ? modified_pages[modified_pages.length - 1] : modified_pages[0];

    // Create a white page (empty)
    const emptyWhitePage = null; // This will be used for page 0

    if (isProductRtlInBook) {
      // For RTL: Add white page, then dedication page, then the rest
      if (hasDedicationImage()) {
        // Dedication WITH image should appear right after the white page
        if (isInIsrael) {
          filteredPages = [
            filteredPages[1],                // dedication with image
            ...filteredPages.slice(0, -2), // everything before the last 2 pages
            filteredPages[filteredPages.length - 1], // keep the last page
            whitePage,
          ];
        } else {
          filteredPages = [
            whitePage,
            filteredPages[1],          // keep index 1
            ...filteredPages.slice(3), // skip index 2, continue from index 3
            whitePage,
          ];
        }
      } else {
        if (isInIsrael) {
          // Dedication WITHOUT image should appear after two whites
          filteredPages = [
            filteredPages[0],                // dedication without image
            ...filteredPages        // the rest
          ];
        } else {
          filteredPages = [
            whitePage,
            ...filteredPages.slice(2), // dedication page without image
            // modified_pages[2],
            modified_pages[modified_pages.length - 1],
            whitePage,
          ];
        }
      }
    } else {
      // LTR: mirror RTL logic
      if (hasDedicationImage()) {
        // Dedication WITH image should appear before the last page
        filteredPages = [
          emptyWhitePage,
          ...filteredPages.slice(0, -2),          // everything except last 2
          filteredPages[filteredPages.length - 1], // dedication with image
          emptyWhitePage, // keep last page
        ];
      } else {
        // Dedication WITHOUT image should appear before the last page
        filteredPages = [
          emptyWhitePage,                 // first white
          ...filteredPages.slice(0, -1),          // everything except last
          filteredPages[filteredPages.length - 1], // last page
        ];
      }
    }

    // Calculate start page for RTL books
    const startPage = isProductRtlInBook ? filteredPages.length + 1 : 0;

    if (isProductRtlInBook) {
      const renderPages = isInIsrael ? filteredPages : [...filteredPages].reverse();
      const totalPages = renderPages.length;
      return (
        // @ts-ignore: Unreachable code error
        <HTMLFlipBook
          key={`book-${isProductRtlInBook ? 'rtl' : 'ltr'}-${bookView}-${forceUpdate}`}
          width={width}
          height={height}
          size="stretch"
          minWidth={minWidth}
          maxWidth={maxWidth}
          minHeight={minHeight}
          startPage={startPage}
          showCover={true}
          maxHeight={maxHeight}
          ref={desktopRef}
          maxShadowOpacity={0.5}
          mobileScrollSupport={true}>
          {/* First Cover */}
          <div className="relative">
            {cover_pages_predefined_texts
              ?.filter((x: any) => x.page == '0')[0]
              ?.predefined_texts?.map((item: any, idx: number) => {
                let dimensions = JSON.parse(
                  cover_pages_predefined_texts.filter(
                    (x: any) => x.page == '1',
                  )[0]['dimensions'],
                )
                return (
                  <div
                    className="texts absolute"
                    key={`${bookView}-page-${idx}-${forceUpdate}`}
                    id={item.X_coord_percent}
                    data-attribute={`${imageWidth}-${item.max_width}-${dimensions.width
                      }-${pt2px(dimensions.width)}`}
                    style={{
                      fontSize:
                        (imageWidth * item.font_size) / pt2px(dimensions.width),
                      textAlign: getTextAlignment(item.text_align),
                      width:
                        (imageWidth * item.max_width) / pt2px(dimensions.width),
                      top: item.Y_coord_percent + '%',
                      lineHeight: item.line_height,
                      whiteSpace: 'pre-wrap',
                      left: item.X_coord_percent * 2 + '%',
                      color: item.color,
                      fontFamily: item.font_face,
                    }}
                    dangerouslySetInnerHTML={{ __html: item.text }}></div>
                )
              })}
            <Image
              key={`${bookView}-${forceUpdate}`}
              alt="cover"
              className="w-full h-full object-cover"
              width={700}
              priority={!isAIStory}
              height={700}
              onLoad={handleImageLoad}
              onError={(e: any) => {
                console.error('Failed to load image:', e);
                e.target.src = '/placeholder.png';
              }}
              src={formatImageUrl(firstCoverImage)}
              unoptimized={true}
            />
            {renderAIGeneratingOverlay(0)}
          </div>

          {renderPages.map((page: any, index: number) => {
            const logicalPageNumber = isInIsrael
              ? totalPages - index
              : index - 1;

            const predefinedEntry = pages_predefined_texts.find(
              (x: any) => x.page === logicalPageNumber.toString()
            );

            const isWhitePage = index === 0;
            const dedicationPageNumber =
              isInIsrael ? (hasDedicationImage() ? '1' : '2') : (hasDedicationImage() ? (filteredPages.length - 2).toString() : (modified_pages.length - 3).toString());

            const dedicationPageIndexInRender = filteredPages.findIndex((p: any) =>
              p === dedicationPage
            );

            const isDedicationPage =
              isInIsrael ?
                (hasDedicationImage() ? (index === (filteredPages.length - 2) - dedicationPageIndexInRender) : (logicalPageNumber.toString() === (dedicationPageNumber))) :
                (index === (filteredPages.length - 1) - dedicationPageIndexInRender);

            const shouldRenderPage = !shouldBlurPage(index, true, isWhitePage);

            return (
              <div className="relative" key={`dedication-image-${index}-${forceUpdate}`}>
                {isWhitePage ? (
                  // White page (empty)
                  <div className="w-full h-full bg-white"></div>
                ) : (
                  // Regular page with image
                  <>
                    <Image
                      key={`${bookView}-${index}-${forceUpdate}`}
                      className={`block relative w-full book-image img-${index} ${!shouldRenderPage ? 'filter blur-sm' : ''}`}
                      alt="book-image"
                      width={700}
                      height={700}
                      onLoad={handleImageLoad}
                      onError={(e: any) => {
                        console.error('Failed to load image:', e);
                        e.target.src = '/placeholder.png';
                      }}
                      priority={!isAIStory}
                      src={formatImageUrl(page)}
                      style={{ display: 'block' }}
                      unoptimized={isAIStory ? true : false}
                    />

                    {/* Render lock overlay for blurred pages - Only when book is loaded */}
                    {renderAIGeneratingOverlay(index)}
                    {renderAIStoryLockOverlay(index)}
                  </>
                )}

                {/* Dedication Text/Image - Only show on dedication page */}
                {shouldRenderPage && isDedicationPage && (() => {
                  // Pick correct dedication entry based on image selection
                  const dedicationEntry = pages_dedication_texts.find(
                    (x: any) =>
                      x.page === dedicationPageNumber
                  );
                  if (!dedicationEntry) {
                    return null;
                  }

                  const dimensions = JSON.parse(dedicationEntry.dimensions);

                  return dedicationEntry.dedication_texts?.map((item: any, idx: number) => {
                    if (!item.text) {
                      // image block (only exists on page 1)
                      return (
                        <div
                          key={`dedication-text-${idx}-${forceUpdate}`}
                          className="absolute z-[20]"
                          style={{
                            top: `${(item.Y_coord / dimensions.height) * 76}%`,
                            left: `${(item.X_coord / dimensions.height) * 74.5}%`,
                            width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                            height: (imageWidth * item.max_width) / pt2px(dimensions.width),
                          }}
                        >
                          {dedicationImageMode === 'ai-image' && aiStoryImage ? (
                            // Render AI story image for dedication
                            <img
                              src={aiStoryImage}
                              alt="AI Story Image"
                              className="object-cover rounded-full w-full h-full"
                            />
                          ) : dedicationImageMode === 'separate-image' && dedicationImage ? (
                            // Render separate dedication image
                            <img
                              src={dedicationImage}
                              alt="Dedication Image"
                              className="object-cover rounded-full w-full h-full"
                            />
                          ) : null}
                        </div>
                      );
                    }

                    // text block
                    return (
                      <div
                        key={`dedication-child-image-${index}-${forceUpdate}`}
                        className="absolute z-[20]"
                        style={{
                          fontSize: Math.max(
                            10,
                            (imageWidth * item.font_size) / pt2px(dimensions.width)
                          ),
                          textAlign: getTextAlignment(item.text_align),
                          width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                          top: item.Y_coord_percent > 0
                            ? `${item.Y_coord_percent}%`
                            : `${(item.Y_coord / dimensions.height) * 70}%`,
                          left: item.X_coord_percent > 0
                            ? `${item.X_coord_percent}%`
                            : `${(item.X_coord / dimensions.height) * 83}%`,
                          color: item.color,
                          lineHeight: item.line_height,
                          whiteSpace: 'pre-wrap',
                          fontFamily: item.font_face,
                        }}
                      >
                        {item.text === 'أدخل النص'
                          ? inputDedication.replace('{basmti}', name ?? product.replace_name)
                          : item.text?.includes('{basmti}')
                            ? item.text.replace('{basmti}', name ?? product.replace_name)
                            : item.text}
                      </div>
                    );
                  });
                })()}

                {/* Predefined Texts - Show on all pages except white page */}
                {shouldRenderPage && !isWhitePage && predefinedEntry?.predefined_texts?.map((item: any, idx: number) => {
                  const dimensions = JSON.parse(predefinedEntry.dimensions);

                  return (
                    <div
                      className="texts absolute"
                      key={`dedication-text-without-image-${idx}-${forceUpdate}`}
                      style={{
                        fontSize: (imageWidth * item.font_size) / pt2px(dimensions.width),
                        textAlign: getTextAlignment(item.text_align),
                        width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                        top: item.Y_coord_percent + '%',
                        left: item.X_coord_percent + '%',
                        color: item.color,
                        lineHeight: item.line_height,
                        whiteSpace: 'pre-wrap',
                        fontFamily: item.font_face,
                      }}
                      dangerouslySetInnerHTML={{ __html: item.text }}
                    ></div>
                  );
                })}
              </div>
            );
          })}

          {/* Last Cover */}
          <div className="relative start-cover">
            {cover_pages_predefined_texts
              ?.filter((x: any) => x.page == '1')[0]
              ?.predefined_texts?.map((item: any, idx: number) => {
                let dimensions = JSON.parse(
                  cover_pages_predefined_texts.filter(
                    (x: any) => x.page == '0',
                  )[0]['dimensions'],
                )
                return (
                  <div
                    className="texts absolute"
                    key={`end-cover-${idx}-${forceUpdate}`}
                    id={item.X_coord_percent}
                    data-attribute={`${imageWidth}-${item.max_width}-${dimensions.width
                      }-${pt2px(dimensions.width)}`}
                    style={{
                      fontSize:
                        (imageWidth * item.font_size) / pt2px(dimensions.width),
                      textAlign: getTextAlignment(item.text_align),
                      width:
                        (imageWidth * item.max_width) / pt2px(dimensions.width),
                      top: item.Y_coord_percent + '%',
                      lineHeight: item.line_height,
                      whiteSpace: 'pre-wrap',
                      left: item.X_coord_percent * 2 + '%',
                      color: item.color,
                      fontFamily: item.font_face,
                    }}
                    dangerouslySetInnerHTML={{ __html: item.text }}></div>
                )
              })}
            <Image
              key={`${bookView}-end-${forceUpdate}`}
              alt="cover"
              className="w-full h-full object-cover"
              width={700}
              height={700}
              onLoad={handleImageLoad}
              onError={(e: any) => {
                console.error('Failed to load image:', e);
                e.target.src = '/placeholder.png';
              }}
              src={formatImageUrl(lastCoverImage)}
            />
            {renderAIGeneratingOverlay(0)}
          </div>
        </HTMLFlipBook>
      )
    } else {
      // If English Language (LTR)
      return (
        // @ts-ignore: Unreachable code error
        <HTMLFlipBook
          key={`book-${isProductRtlInBook ? 'rtl' : 'ltr'}-${bookView}-${forceUpdate}`}
          width={width}
          height={height}
          size="stretch"
          ref={desktopRef}
          minWidth={minWidth}
          maxWidth={maxWidth}
          minHeight={minHeight}
          startPage={0}
          showCover={true}
          maxHeight={maxHeight}
          maxShadowOpacity={0.5}
          mobileScrollSupport={true}
        >
          {/* Back Cover */}
          <div className="relative">
            {cover_pages_predefined_texts
              ?.find((x: any) => x.page == '1')
              ?.predefined_texts?.map((item: any, idx: number) => {
                const dimensions = JSON.parse(
                  cover_pages_predefined_texts.find((x: any) => x.page == '1')
                    ?.dimensions || '{}'
                );
                return (
                  <div
                    className="texts absolute"
                    key={`back-cover-${idx}-${forceUpdate}`}
                    style={{
                      fontSize:
                        (imageWidth * item.font_size) / pt2px(dimensions.width),
                      textAlign: getTextAlignment(item.text_align),
                      width:
                        (imageWidth * item.max_width) / pt2px(dimensions.width),
                      top: item.Y_coord_percent + '%',
                      lineHeight: item.line_height,
                      whiteSpace: 'pre-wrap',
                      left: item.X_coord_percent * 2 + '%',
                      color: item.color,
                      fontFamily: item.font_face,
                      direction: 'ltr',
                    }}
                    dangerouslySetInnerHTML={{ __html: item.text }}
                  ></div>
                );
              })}
            <Image
              key={`${bookView}-back-${forceUpdate}`}
              alt="cover"
              className="w-full h-full object-cover"
              width={700}
              priority={!isAIStory}
              height={700}
              onLoad={handleImageLoad}
              onError={(e: any) => {
                console.error('Failed to load image:', e);
                e.target.src = '/placeholder.png';
              }}
              src={formatImageUrl(lastCoverImage)}
            />
            {renderAIGeneratingOverlay(0)}
          </div>

          {/* Main Pages */}
          {[...filteredPages].reverse().map((page: any, index: number) => {
            const isWhitePage = index === 0;
            const isDedicationPage = index === 1; // second page is dedication page
            const dedicationEntry = isDedicationPage
              ? pages_dedication_texts.find(
                (x: any) =>
                  x.page === (hasDedicationImage() ? '1' : '2')
              )
              : null;

            const predefinedEntry = pages_predefined_texts.find(
              (x: any) => x.page == (index + 1).toString()
            );

            const shouldRenderPage = !shouldBlurPage(index, false, isWhitePage);

            return (
              <div
                className="relative"
                key={`dedication-page-${index}-${forceUpdate}`}
              >
                {isWhitePage ? (
                  <div className="w-full h-full bg-white"></div>
                ) : (
                  <>
                    <Image
                      className={`block w-full book-image img-${index} ${!shouldRenderPage ? 'filter blur-sm' : ''}`}
                      alt="book-image"
                      width={700}
                      height={700}
                      onLoad={handleImageLoad}
                      onError={(e: any) => {
                        console.error('Failed to load image:', e);
                        e.target.src = '/placeholder.png';
                      }}
                      priority={!isAIStory}
                      src={formatImageUrl(page)}
                      style={{ display: 'block' }}
                    />

                    {/* Render lock overlay for blurred pages - Only when book is loaded */}
                    {renderAIGeneratingOverlay(index)}
                    {renderAIStoryLockOverlay(index)}
                  </>
                )}

                {/* Dedication Text/Image */}
                {shouldRenderPage && isDedicationPage && dedicationEntry?.dedication_texts?.map((item: any, idx: number) => {
                  const dimensions = JSON.parse(dedicationEntry.dimensions);
                  const isImageBlock = !item.text;

                  if (isImageBlock) {
                    return (
                      <div
                        key={`dedication-child-image-${idx}-${forceUpdate}`}
                        className="texts absolute z-[20]"
                        style={{
                          top: `${(item.Y_coord / dimensions.height) * 76}%`,
                          left: `${(item.X_coord / dimensions.width) * 74.5}%`,
                          width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                          height: (imageWidth * item.max_width) / pt2px(dimensions.width),
                        }}
                      >
                        {dedicationImageMode === 'ai-image' && aiStoryImage ? (
                          // Render AI story image for dedication
                          <img
                            src={aiStoryImage}
                            alt="AI Story Image"
                            className="object-cover rounded-full w-full h-full"
                          />
                        ) : dedicationImageMode === 'separate-image' && dedicationImage ? (
                          // Render separate dedication image
                          <img
                            src={dedicationImage}
                            alt="Dedication Image"
                            className="object-cover rounded-full w-full h-full"
                          />
                        ) : null}
                      </div>
                    );
                  }

                  return (
                    <div
                      className="texts absolute z-[20]"
                      key={`dedication-text-${idx}-${forceUpdate}`}
                      style={{
                        fontSize: Math.max(
                          10,
                          (imageWidth * item.font_size) / pt2px(dimensions.width)
                        ),
                        textAlign: getTextAlignment(item.text_align),
                        width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                        top: item.Y_coord_percent + '%',
                        left: item.X_coord_percent + '%',
                        color: item.color,
                        lineHeight: item.line_height,
                        whiteSpace: 'pre-wrap',
                        fontFamily: item.font_face,
                      }}
                    >
                      {item.text === 'أدخل النص'
                        ? inputDedication.replace('{basmti}', name ?? product.replace_name)
                        : item.text?.includes('{basmti}')
                          ? item.text.replace('{basmti}', name ?? product.replace_name)
                          : item.text}
                    </div>
                  );
                })}

                {/* Predefined Texts */}
                {shouldRenderPage && predefinedEntry?.predefined_texts?.map((item: any, idx: number) => {
                  const dimensions = JSON.parse(predefinedEntry.dimensions);
                  return (
                    <div
                      className="texts absolute"
                      key={`predef-${idx}-${forceUpdate}`}
                      style={{
                        fontSize:
                          (imageWidth * item.font_size) / pt2px(dimensions.width),
                        textAlign: getTextAlignment(item.text_align),
                        width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                        top: item.Y_coord_percent + '%',
                        lineHeight: item.line_height,
                        whiteSpace: 'pre-wrap',
                        color: item.color,
                        left: item.X_coord_percent + '%',
                        fontFamily: item.font_face,
                        direction: 'ltr',
                      }}
                      dangerouslySetInnerHTML={{ __html: item.text }}
                    ></div>
                  );
                })}
              </div>
            );
          })}

          {/* Front Cover */}
          <div className="relative start-cover">
            {cover_pages_predefined_texts
              ?.find((x: any) => x.page == '0')
              ?.predefined_texts?.map((item: any, idx: number) => {
                const dimensions = JSON.parse(
                  cover_pages_predefined_texts.find((x: any) => x.page == '0')
                    ?.dimensions || '{}'
                );
                return (
                  <div
                    className="texts absolute"
                    key={`${bookView}-page-${idx}-${forceUpdate}`}
                    style={{
                      fontSize:
                        (imageWidth * item.font_size) / pt2px(dimensions.width),
                      textAlign: getTextAlignment(item.text_align),
                      width:
                        (imageWidth * item.max_width) / pt2px(dimensions.width),
                      top: item.Y_coord_percent + '%',
                      lineHeight: item.line_height,
                      whiteSpace: 'pre-wrap',
                      left: item.X_coord_percent * 2 + '%',
                      color: item.color,
                      fontFamily: item.font_face,
                      direction: 'ltr',
                    }}
                    dangerouslySetInnerHTML={{ __html: item.text }}
                  ></div>
                );
              })}
            <Image
              key={`${bookView}-end-cover-${forceUpdate}`}
              alt="cover"
              className="w-full h-full object-cover"
              width={700}
              height={700}
              onLoad={handleImageLoad}
              onError={(e: any) => {
                console.error('Failed to load image:', e);
                e.target.src = '/placeholder.png';
              }}
              src={formatImageUrl(firstCoverImage)}
            />
            {renderAIGeneratingOverlay(0)}
          </div>
        </HTMLFlipBook>
      )
    }
  }
  return <></>
}